import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import { ReactComponent as HandCoinsIcon } from '../../assets/images/svg/hand-coins.svg';
import Paragraph from '../Paragraph/Paragraph';
import themeColors from '../../assets/styles/themeColors';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import Button from '../Button/Button';
import ConfettiGif from '../../assets/images/svg/confetti.svg';
import config from '../../config';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import TransactionDetails from '../Modals/Transaction/TransactionDetails';
import {
  emptyPortfolioTransactionSingle,
  portfolioTransactionsSingleFetch,
} from '../../store/actions/portfolio/portfolioActions';
import {
  selectPortfolioSingleTransaction,
  selectPortfolioSingleTransactionError,
} from '../../store/selectors/portfolioTransactionsSelector';
import { PORTFOLIO_TRANSACTIONS_SINGLE_LOADING } from '../../store/actions/portfolio/portfolioActionConstants';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import { getCurrentAccountUid } from '../../store/selectors/accountSelectors';
import SectionLoader from '../Loader/SectionLoader';
import { ReactComponent as Close } from '../../assets/images/svg/close.svg';
import IconButton from '../IconButton/IconButton';
import { variables } from '../../assets/styles/variables';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${pxToRem(16)};
  overflow: hidden;
  background-image: url(${ConfettiGif});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #fff;
`;

const Title = styled.h2`
  margin: ${pxToRem(16)} 0 ${pxToRem(16)} 0;
  font-size: ${pxToRem(20)};
  font-weight: 500;
`;

const Text = styled.p`
  font-size: ${pxToRem(16)};
  margin-bottom: ${pxToRem(4)};
`;

const Highlight = styled.span`
  font-weight: 700;
`;

const Price = styled.div`
  font-size: ${pxToRem(20)};
  margin-bottom: ${pxToRem(20)};
`;

const FinalPrice = styled.p`
  font-size: ${pxToRem(14)};
  margin-bottom: ${pxToRem(20)};
  color: ${themeColors.colorTextSecondary};
`;

const SavingsBox = styled.div`
  background-color: ${themeColors.colorPrimaryLight};
  padding: ${pxToRem(10)};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid #cceedb;
  border-radius: ${pxToRem(4)};
  margin-bottom: ${pxToRem(20)};
`;

const SavingInfo = styled(Paragraph)`
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorSuccess};
  svg {
    display: inline-block;
    vertical-align: middle;
    margin-right: ${pxToRem(4)};
  }
`;

const ButtonContainer = styled.div`
  position: absolute;
  bottom: 20px;
  width: 95%;
  display: flex;
  justify-content: center;
`;

const Image = styled.img`
  width: ${pxToRem(180)};
  height: ${pxToRem(180)};
  object-fit: contain;
`;

export const ModalClose = styled(IconButton)`
  &,
  svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
    margin-left: auto;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    &,
    svg {
      width: ${pxToRemMd(24)};
      height: ${pxToRemMd(24)};
    }
  }
`;

const OrderConfirmation = ({
  wizardData,
  onButtonClick,
  setGoBackHandler,
  stepIndex,
  currentStepIndex,
  setWizardTitle,
  isPostPaidVisible,
  closeModal,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    storageProduct,
    calculation,
    selectedPaymentMethod,
    transactionUid,
  } = wizardData;
  const { RootUrl } = useSelector(selectSettings);
  const accountUid = useSelector(getCurrentAccountUid);

  useEffect(() => {
    if (stepIndex === currentStepIndex) {
      setGoBackHandler(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, currentStepIndex]);

  useEffect(() => {
    setWizardTitle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const imageUrl = config.algoliaSearch.imageCode
    .replace('{product_code}', storageProduct.ProductCode)
    .replace('{base_url}', RootUrl);

  const [isTransactionDetailsOpen, setIsTransactionDetailsOpen] = useState(
    false,
  );
  const singleTransaction = useSelector(selectPortfolioSingleTransaction);
  const singleTransactionError = useSelector(
    selectPortfolioSingleTransactionError,
  );
  const singleTransactionLoading = useSelector(
    selectIsLoadingByActionType(PORTFOLIO_TRANSACTIONS_SINGLE_LOADING),
  );

  const onTransactionCardClick = useCallback(
    (transactionUid) => {
      dispatch(
        portfolioTransactionsSingleFetch({
          accountUid,
          transactionUid,
        }),
      );
      setIsTransactionDetailsOpen(true);
    },
    [accountUid, dispatch],
  );

  return (
    <>
      <SectionLoader isLoading={singleTransactionLoading}>
        <Container>
          <ModalClose onClick={closeModal} data-cy="button-close-modal">
            <Close />
          </ModalClose>
          <Image src={encodeURI(imageUrl)} alt="Product Name Value Etc..." />

          <Title>{t('buyWizard.congratulations')}</Title>
          <Text>{t('buyWizard.orderCompleteTitle')}</Text>
          <Price>
            <Highlight>
              {calculation?.quantity} × {storageProduct?.ShortCaption}
            </Highlight>
            <br />
            <FinalPrice>
              {' '}
              at {formatMoneyNumeral(calculation?.estimatedOrderTotal)}
            </FinalPrice>
          </Price>
          {isPostPaidVisible && selectedPaymentMethod?.PaymentMethodType !== 3 && (
            <SavingsBox>
              <SavingInfo>
                <HandCoinsIcon />
                {t('buyWizard.savingInfo3')}
                <b> {calculation?.paymentTypeDiscount} (4%)</b>
              </SavingInfo>
            </SavingsBox>
          )}
          <ButtonContainer>
            <Button
              variant="primaryOutlined"
              textTransform="none"
              style={{ width: '100%' }}
              onClick={() => {
                onTransactionCardClick(transactionUid);
              }}
            >
              {t('buyWizard.viewOrder')}
            </Button>
          </ButtonContainer>
        </Container>
      </SectionLoader>

      <TransactionDetails
        isOpen={!isEmpty(singleTransaction) && isTransactionDetailsOpen}
        onClose={() => {
          dispatch(emptyPortfolioTransactionSingle());
          setIsTransactionDetailsOpen(false);
          onButtonClick();
        }}
        transaction={singleTransaction}
        error={singleTransactionError}
      />
    </>
  );
};

OrderConfirmation.propTypes = {
  wizardData: PropTypes.shape({
    storageProduct: PropTypes.shape({
      ProductCaption: PropTypes.string,
      ProductPrice: PropTypes.number,
      Locations: PropTypes.arrayOf(PropTypes.shape({})),
      ProductCode: PropTypes.string,
      ShortCaption: PropTypes.string,
    }),
    calculation: PropTypes.shape({
      paymentTypeDiscount: PropTypes.number,
      estimatedOrderTotal: PropTypes.number,
      quantity: PropTypes.number,
    }),
    selectedPaymentMethod: PropTypes.shape({
      PaymentMethodType: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    }),
    transactionUid: PropTypes.string,
  }),
  onButtonClick: PropTypes.func,
  setWizardTitle: PropTypes.func,
  setGoBackHandler: PropTypes.func,
  stepIndex: PropTypes.number,
  currentStepIndex: PropTypes.number,
  isPostPaidVisible: PropTypes.bool,
  closeModal: PropTypes.func,
};

export default OrderConfirmation;
